import React, { useCallback, useEffect, useRef, useState } from "react";
import MonacoEditor, { type OnMount } from "@monaco-editor/react";
import type { ComponentDefinition } from "../../Pages/pageComponentLibrary";
import { getDefaultBlockMarkdown } from "../../Pages/pageComponentLibrary";
import {
	getComponentBlockRanges,
	isCursorInsideAnyComponentBlock,
	isLineTitleOrText,
	normalizeLineTerminators
} from "./markdownPanelUtils";

/** Line has ::: followed by a word (for unknown-component decoration). */
const BLOCK_OPEN_LINE_REGEX = /^:::\s+(\w+)/;

type PageEditorMarkdownPanelProps = {
	editable: boolean;
	value: string;
	error: string | null;
	onChange: (value: string) => void;
	editorOptions: Record<string, unknown>;
	componentDefinitionMap?: Map<string, ComponentDefinition>;
	/** Optional fixed editor height for embedded use-cases (px or CSS value). */
	editorHeight?: number | string;
	/** Page title to show in the markdown panel header (draft.title). */
	pageTitle?: string | null;
	/** Page description to show in the markdown panel header (draft.metadata.description). */
	pageDescription?: string | null;
	/** Opens the component library modal (same as blocks mode). */
	onOpenComponentLibrary?: () => void;
	/** Ref the parent sets so when user picks a component in the modal we insert at cursor. */
	insertComponentRef?: React.MutableRefObject<((def: ComponentDefinition) => void) | null>;
};

/**
 * Editable Markdown view: frontmatter (--- slug, language ---), optional # title, then ## Section or ::: ComponentType ... :::.
 * "Add component" opens the same component library modal as blocks mode; picking a component inserts its block at the cursor.
 */
/** Markdown editing panel with Monaco editor, component insertion and live updates. */
const PageEditorMarkdownPanel: React.FC<PageEditorMarkdownPanelProps> = ({
	editable,
	value,
	error,
	onChange,
	editorOptions,
	componentDefinitionMap,
	editorHeight,
	pageTitle,
	pageDescription,
	onOpenComponentLibrary,
	insertComponentRef
}) => {
	const editorHeightValue =
		typeof editorHeight === "number" ? `${editorHeight}px` : editorHeight;
	const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
	const decorationIdsRef = useRef<string[]>([]);
	const [addComponentLocked, setAddComponentLocked] = useState(false);

	const updateAddComponentLocked = useCallback(() => {
		const editor = editorRef.current;
		const model = editor?.getModel();
		if (!editor || !model) {
			setAddComponentLocked(false);
			return;
		}
		const position = editor.getPosition();
		if (!position) {
			setAddComponentLocked(false);
			return;
		}
		const lines = model.getLinesContent();
		const lineNumber = position.lineNumber;
		const insideAnyComponent = isCursorInsideAnyComponentBlock(lines, lineNumber);
		const lineContent = lines[lineNumber - 1] ?? "";
		const titleOrText = isLineTitleOrText(lineContent);
		setAddComponentLocked(insideAnyComponent || titleOrText);
	}, []);

	const updateUnknownComponentDecorations = useCallback(() => {
		const editor = editorRef.current;
		if (!editor || !componentDefinitionMap) return;
		const model = editor.getModel();
		if (!model) return;

		const knownTypes = new Set(componentDefinitionMap.keys());
		const lines = model.getLinesContent();
		const newDecorations: Array<{
			range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number };
			options: { inlineClassName: string };
		}> = [];

		for (let i = 0; i < lines.length; i++) {
			const match = lines[i].match(BLOCK_OPEN_LINE_REGEX);
			if (!match) continue;
			const componentName = match[1];
			if (knownTypes.has(componentName)) continue;
			const startCol = lines[i].indexOf(componentName) + 1;
			const endCol = startCol + componentName.length;
			newDecorations.push({
				range: { startLineNumber: i + 1, startColumn: startCol, endLineNumber: i + 1, endColumn: endCol },
				options: { inlineClassName: "unknown-component-markdown" }
			});
		}

		decorationIdsRef.current = editor.deltaDecorations(decorationIdsRef.current, newDecorations);
	}, [componentDefinitionMap]);

	const insertComponentAtCursor = useCallback(
		(definition: ComponentDefinition) => {
			const editor = editorRef.current;
			const model = editor?.getModel();
			if (!editor || !model) return;
			const position = editor.getPosition();
			if (!position) return;
			const lineNumber = position.lineNumber;
			const blockBody = getDefaultBlockMarkdown(definition);
			const insertText = blockBody.startsWith("```") ? blockBody : `::: ${blockBody}`;
			const lineContent = model.getLineContent(lineNumber);
			editor.executeEdits("component-insert", [
				{
					range: {
						startLineNumber: lineNumber,
						startColumn: 1,
						endLineNumber: lineNumber,
						endColumn: lineContent.length + 1
					},
					text: insertText
				}
			]);
			editor.setPosition({ lineNumber: lineNumber + 1, column: 1 });
			editor.revealPosition({ lineNumber: lineNumber + 1, column: 1 });
			onChange(model.getValue());
		},
		[onChange]
	);

	useEffect(() => {
		if (!insertComponentRef) return;
		insertComponentRef.current = insertComponentAtCursor;
		return () => {
			insertComponentRef.current = null;
		};
	}, [insertComponentRef, insertComponentAtCursor]);

	useEffect(() => {
		if (!componentDefinitionMap) return;
		const t = setTimeout(() => updateUnknownComponentDecorations(), 50);
		return () => clearTimeout(t);
	}, [value, componentDefinitionMap, updateUnknownComponentDecorations]);

	const handleEditorMount: OnMount = useCallback(
		(editor) => {
			editorRef.current = editor;
			const model = editor.getModel();
			if (!model) return;

			model.onDidChangeContent(() => {
				updateUnknownComponentDecorations();
				updateAddComponentLocked();
			});
			editor.onDidChangeCursorPosition(() => updateAddComponentLocked());
			updateUnknownComponentDecorations();
			updateAddComponentLocked();
		},
		[updateUnknownComponentDecorations, updateAddComponentLocked]
	);

	return (
		<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4 flex flex-col h-full min-h-[360px]">
			<style>{`.monaco-editor .unknown-component-markdown { color: #dc2626; text-decoration: wavy underline; text-underline-offset: 2px; }`}</style>
			<div className="flex flex-wrap items-start justify-between gap-2">
				<div>
					<p className="text-xs uppercase tracking-widest text-slate-400">Markdown</p>
					{pageTitle != null && pageTitle !== "" && (
						<p className="text-lg font-semibold text-slate-900 mt-0.5" data-testid="markdown-panel-page-title">{pageTitle}</p>
					)}
					{pageDescription != null && pageDescription !== "" && (
						<p className="text-sm text-slate-600 mt-0.5" data-testid="markdown-panel-page-description">{pageDescription}</p>
					)}
				</div>
				{editable && onOpenComponentLibrary && (
					<button
						type="button"
						onClick={onOpenComponentLibrary}
						disabled={addComponentLocked}
						title={addComponentLocked ? "Move cursor to a blank line to add a component" : undefined}
						className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 shrink-0 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<span aria-hidden>+</span>
						Add component
					</button>
				)}
			</div>
			<div
				className="flex-1 min-h-[240px] overflow-hidden rounded-lg border border-slate-200 relative"
				style={editorHeightValue ? { height: editorHeightValue, minHeight: editorHeightValue } : undefined}
			>
				<MonacoEditor
					height={editorHeightValue ?? "100%"}
					language="markdown"
					theme="vs"
					value={normalizeLineTerminators(value)}
					options={{ ...editorOptions, readOnly: !editable }}
					onChange={(v) => onChange(normalizeLineTerminators(v ?? ""))}
					onMount={handleEditorMount}
				/>
			</div>
			{error ? (
				<p className="text-xs font-semibold text-rose-600">{error}</p>
			) : (
				<p className="text-xs text-slate-500">Valid Markdown. Edits update the preview.</p>
			)}
		</div>
	);
};

export default PageEditorMarkdownPanel;
